/**
 * The field dictionary — the single definition of what each measurement field IS: its unit, its
 * input's step, its bounds, its value type, whether it is visible without being asked for, and its
 * position in the ONE canonical order.
 *
 * It reaches the app as a VENDORED CONTRACT FILE, not over the network.
 * `measurement-fields-contract.json` is a byte copy of the backend's committed
 * `docs/contracts/measurement-fields-contract.json`, emitted from the production dictionary;
 * `fieldDictionary.test.ts` goes red when the two drift. Re-vendor it with:
 *
 *   cp ../train-with-me-app/docs/contracts/measurement-fields-contract.json \
 *      src/shared/measurement/measurement-fields-contract.json
 *
 * ── The register said `entities/exercise-library`, and that path was an import error ──────────
 * `docs/contracts/README.md` used to send this file to
 * `src/entities/exercise-library/model/__fixtures__/`. It cannot go there: the dictionary is what
 * reads it, the dictionary belongs in `shared/` (both entities and both PR-B2C features consume
 * it), and **`shared` may not import from `entities`** — the layer direction is enforced by
 * `eslint-plugin-boundaries`. It lives beside its one reader instead, which is also where mobile
 * keeps its copy. The register was corrected to match in PR-B2C.
 *
 * NOTHING outside this module reads the JSON. That is the seam: the day the rows become
 * trainer-defined, only what stands behind these functions changes and every consumer is untouched.
 *
 * No React, no DOM, no query hook, no i18next.
 */

import contract from './measurement-fields-contract.json';
import {
  parseMeasurementField,
  parseMeasurementFields,
  type MeasurementFieldKey,
  type MeasurementValueType,
} from './measurementField';

/** Everything known about one measurement field. */
export interface MeasurementFieldDefinition {
  readonly field: MeasurementFieldKey;
  readonly valueType: MeasurementValueType;
  /** Display unit ("kg", "m", "s", "km/h"), or `null` for a unitless field (reps, rounds, RPE, RIR). */
  readonly unit: string | null;
  /** The input's increment. `null` for a field no stepper can drive — the text notation. */
  readonly step: number | null;
  readonly min: number | null;
  readonly max: number | null;
  /**
   * The grid a MACHINE-COMPUTED value lands on when a progression rule advances this column, and the
   * range it is held inside afterwards. A DIFFERENT grid from `step`/`min`/`max`, which bound what a
   * trainer may TYPE: weight is typed in 0,5 kg and progressed on 1,25 kg plates, and reps are typed
   * from 0 while a progressed rep count floors at 1 — a prescription of nothing is the absence of a
   * set rather than an easier one.
   *
   * These three are the halves of one arithmetic that lives in two runtimes: the backend applies
   * them in `ProgressionCalculator.Advance`, this app in `snapToProgressionGrid`, and
   * `progression-projection-parity.json` is what keeps the two copies from drifting. `null` marks a
   * column with no grid at all (the text notation), which a projection rides through untouched.
   */
  readonly progressionStep: number | null;
  readonly progressionMin: number | null;
  readonly progressionMax: number | null;
  /** Shown without being asked for — the builder's starting column set on a fresh exercise. */
  readonly visibleByDefault: boolean;
  readonly countsTowardRecords: boolean;
  /**
   * Whether reaching this column is part of DELIVERING a set — the question an execution-gated rule
   * asks before it advances (plan-progression R3). The work columns say yes; the two effort scales
   * say no, because RPE and RIR are the client's report of how the set felt rather than work they
   * were set, and a rule that waited on one would gate a prescription on an opinion.
   *
   * It is also the gate on which columns a rule may BAND: a band names the column the client climbs
   * to before the rule pays out, so a column that decides nothing cannot be one.
   */
  readonly decidesProgression: boolean;
  /** Position in the one canonical order. */
  readonly order: number;
}

/**
 * Built once at import.
 *
 * A contract that does not cover every key this build knows is a BROKEN BUILD, not a runtime
 * condition to defend against on every read — so `measurementFieldDefinition` throws rather than
 * returning `undefined`. The JSON is in the bundle: it is either right or it is not, and pushing a
 * null check into every caller would buy nothing but noise.
 */
const DEFINITIONS: ReadonlyMap<MeasurementFieldKey, MeasurementFieldDefinition> = (() => {
  const byField = new Map<MeasurementFieldKey, MeasurementFieldDefinition>();
  for (const raw of contract.fields) {
    const field = parseMeasurementField(raw.field);
    if (field === null) continue;
    byField.set(field, {
      field,
      valueType: raw.valueType as MeasurementValueType,
      unit: raw.unit,
      step: raw.step,
      min: raw.min,
      max: raw.max,
      progressionStep: raw.progressionStep,
      progressionMin: raw.progressionMin,
      progressionMax: raw.progressionMax,
      visibleByDefault: raw.visibleByDefault,
      countsTowardRecords: raw.countsTowardRecords,
      decidesProgression: raw.decidesProgression,
      order: raw.order,
    });
  }
  return byField;
})();

/** Every definition the dictionary holds, in canonical order. */
const ORDERED: readonly MeasurementFieldDefinition[] = [...DEFINITIONS.values()].sort(
  (a, b) => a.order - b.order,
);

const ORDER_INDEX: ReadonlyMap<MeasurementFieldKey, number> = new Map(
  ORDERED.map((definition, index) => [definition.field, index]),
);

/** Every field definition the dictionary holds, in canonical order. */
export function allMeasurementFieldDefinitions(): readonly MeasurementFieldDefinition[] {
  return ORDERED;
}

/** The definition of one field. Total by construction — see `DEFINITIONS`. */
export function measurementFieldDefinition(field: MeasurementFieldKey): MeasurementFieldDefinition {
  const definition = DEFINITIONS.get(field);
  if (definition === undefined) {
    throw new Error(
      `measurement dictionary: no definition for "${field}" — the vendored contract is stale`,
    );
  }
  return definition;
}

/**
 * Sort into the dictionary's order. **The ONE sort in the app.**
 *
 * No surface applies an order of its own. Mobile's own defect is the reason the rule is stated this
 * strongly: the same exercise read "REPS · KG · RPE" when prescribed and "KG × REPS · RPE" when
 * reviewed, because two surfaces each sorted their own way.
 */
export function sortMeasurementFields(
  fields: readonly MeasurementFieldKey[],
): MeasurementFieldKey[] {
  return [...fields].sort((a, b) => (ORDER_INDEX.get(a) ?? 0) - (ORDER_INDEX.get(b) ?? 0));
}

/**
 * An exercise's `measurementFields` off the wire, as keys in canonical order — the ONE entry point
 * every mapper uses. **Empty in, empty out**: an exercise that measures nothing keeps measuring
 * nothing, and no default is ever substituted.
 */
export function measurementFieldsFromWire(
  values: readonly (string | null | undefined)[] | null | undefined,
): MeasurementFieldKey[] {
  return sortMeasurementFields(parseMeasurementFields(values));
}

/**
 * True when the field is an EFFORT scale — a bounded number on a fixed step (RPE, RIR).
 *
 * Read off the VALUE TYPE, never a field list, so it stays right when the dictionary gains another
 * effort scale. It is the same two columns `decidesProgression` says no about, and the two are
 * separate questions on purpose: this one is about how the number is written, that one about whether
 * reaching it is part of delivering the set.
 */
export function isEffortField(field: MeasurementFieldKey): boolean {
  return measurementFieldDefinition(field).valueType === 'BoundedNumberWithStep';
}

/**
 * The fields the dictionary shows without being asked — where a freshly inserted exercise starts
 * before it carries a single value.
 */
export function defaultVisibleFields(): MeasurementFieldKey[] {
  return ORDERED.filter((definition) => definition.visibleByDefault).map(
    (definition) => definition.field,
  );
}
